import { clientLogger } from "@ka-astro/utils";
import { actions } from "astro:actions";
import { useState } from "react";
import type { JSX } from "react/jsx-runtime";
import type { AdGallerySlide } from "../../../../actions/ad/adGallery";
import { AdGalleryCarousel } from "./AdGalleryCarousel/AdGalleryCarousel";
import AdGalleryHeader from "./AdGalleryHeader";
import { getNextPage, getPreviousPage, hasPageBeenFetched } from "./utils";

type Props = {
  adGallery: AdGallerySlide[];
  initialPage: number;
  originForTracking: string;
};

export function AdGallery({
  adGallery,
  initialPage,
  originForTracking,
}: Props): JSX.Element {
  const { numPages, referenceLocationId } = adGallery[0];
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [slides, setSlides] = useState(adGallery);
  const [slideDirection, setSlideDirection] = useState("");

  async function fetchPage(page: number): Promise<AdGallerySlide> {
    try {
      const { data } = await actions.homepageWeb.adGallery.getAdGallery({
        locationId: referenceLocationId,
        page,
      });
      return data as AdGallerySlide;
    } catch (error) {
      clientLogger.error("Failed to fetch gallery data:", error);
      throw error;
    }
  }

  async function openPreviousPage(): Promise<void> {
    const previousPage = getPreviousPage(currentPage, numPages);
    const previousPageToFetch = getPreviousPage(previousPage, numPages);
    if (!hasPageBeenFetched(previousPageToFetch, slides)) {
      const slide = await fetchPage(previousPageToFetch);
      setSlides((prevSlides) => [slide, ...prevSlides]);
    }
    setCurrentPage(previousPage);
    setSlideDirection("left");
  }

  async function openNextPage(): Promise<void> {
    const nextPage = getNextPage(currentPage, numPages);
    const nextPageToFetch = getNextPage(nextPage, numPages);
    if (!hasPageBeenFetched(nextPageToFetch, slides)) {
      const slide = await fetchPage(nextPageToFetch);
      setSlides((prevSlides) => [...prevSlides, slide]);
    }
    setCurrentPage(nextPage);
    setSlideDirection("right");
  }

  return (
    <>
      <AdGalleryHeader
        openPreviousPage={openPreviousPage}
        openNextPage={openNextPage}
        numPages={numPages}
        originForTracking={originForTracking}
      />
      {slides && slides.length > 0 && (
        <AdGalleryCarousel
          slides={slides}
          currentPage={currentPage}
          slideDirection={slideDirection}
          numPages={numPages}
          originForTracking={originForTracking}
        />
      )}
    </>
  );
}
